import { useCallback } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut as firebaseSignOut,
  OAuthProvider,
  reauthenticateWithPopup,
} from "firebase/auth";
import { ref, get, remove } from "firebase/database";
import { auth, db } from "../api/firebase";
import { useAuth } from "../helpers/Auth";
import { useAlert } from "../contexts/AlertContext";
import { deleteFileFromURI } from "../helpers/storageHelpers";
import { useNavigate } from "react-router";
import type { Project } from "../types";

const UNKNOWN_ERROR = "Unknown Error";

const useAuthActions = () => {
  const { effectiveUID } = useAuth();
  const { showError, clearAlert } = useAlert();
  const navigate = useNavigate();

  const userPath = () => `/userdata/${effectiveUID}`;

  const attemptLogin = useCallback(
    async (formProps: { email: string; password: string }) => {
      clearAlert();
      try {
        await signInWithEmailAndPassword(auth, formProps.email, formProps.password);
        navigate("/projects");
      } catch (err: any) {
        if (err) {
          showError(err.message);
        }
      }
    },
    [clearAlert, showError, navigate]
  );

  const signOut = useCallback(async () => {
    firebaseSignOut(auth).catch((e) => console.error("Error", e.message));
  }, []);

  const attemptSignup = useCallback(
    async (formProps: { email: string; password: string }) => {
      clearAlert();
      try {
        await createUserWithEmailAndPassword(auth, formProps.email, formProps.password);
        navigate("/signedup");
      } catch (err: any) {
        if (err) {
          showError(err.message || UNKNOWN_ERROR);
        }
      }
    },
    [clearAlert, showError, navigate]
  );

  const deleteImagesFromProject = (project_to_delete: Project) => {
    if (project_to_delete?.photo) {
      deleteFileFromURI(project_to_delete.photo);
    }
    project_to_delete?.photos &&
      project_to_delete.photos.forEach((photo) => {
        if (photo?.photo) {
          deleteFileFromURI(photo.photo);
        }
        if (photo?.photo256?.uri) {
          deleteFileFromURI(photo.photo256.uri);
        }
        if (photo?.photo1024?.uri) {
          deleteFileFromURI(photo.photo1024.uri);
        }
      });
  };

  const deleteUser = useCallback(
    async (provider_id: string, email: string, password?: string) => {
      const doDelete = async () => {
        const snapshot = await get(ref(db, userPath() + "/"));
        const projects = snapshot.val()?.projects;
        if (projects) {
          Object.values(projects).forEach((project) => {
            deleteImagesFromProject(project as Project);
          });
        }
        remove(ref(db, userPath()));
        const user = auth.currentUser;
        user?.delete().catch((e) => {
          console.error("Can't delete User - need to re-authenticate first", e);
        });
      };

      if (typeof password !== "undefined") {
        try {
          await signInWithEmailAndPassword(auth, email, password);
          await doDelete();
        } catch (error) {
          showError("Incorrect Password");
        }
      } else {
        if (provider_id !== "apple.com") {
          const provider = new OAuthProvider(provider_id);
          await reauthenticateWithPopup(auth.currentUser!, provider);
        }
        await doDelete();
      }
    },
    [effectiveUID, showError]
  );

  return { attemptLogin, signOut, attemptSignup, deleteUser };
};

export default useAuthActions;
